import { cn } from "../lib/utils.ts";

// Role-aware chat message primitives shared between the add-in task pane and
// the marketing site. Keep these purely presentational — no data fetching,
// no side effects — so both consumers can compose them freely.

type Role = "user" | "assistant";

export function Message({
  role,
  className,
  children,
  ...rest
}: {
  role: Role;
  className?: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-role={role}
      className={cn(
        "group flex w-full gap-2",
        role === "user" ? "justify-end" : "justify-start",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function MessageContent({
  role,
  className,
  children,
}: {
  role: Role;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 min-w-0",
        role === "user" ? "items-end max-w-[85%]" : "w-full",
        className,
      )}
    >
      {children}
    </div>
  );
}

// The primary-colored text container used inside a user message. Split out
// so marketing mockups can render a static bubble without pulling in the
// add-in's interactive MessageBubble (which touches snapshots, tRPC, etc.).
export function UserBubble({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "bg-[var(--color-primary)] text-[var(--color-primary-foreground)] px-3 py-1.5 rounded-lg text-sm whitespace-pre-wrap break-words",
        className,
      )}
    >
      {children}
    </div>
  );
}
